import {ref} from 'vue';
import {defineStore} from 'pinia';
import {useAsyncData} from '#imports';
import {useErrorHandler} from '@malt/utils';
import {AffidavitApi} from '@api';
import type {DocumentTypeResource} from '@api';
import {apiConfiguration} from '~/configuration/apiConfiguration';

export interface AffidavitData {
    firstName: string;
    lastName: string;
    companyName: string;
    registrationNumber: string;
    formattedAddress: string;
    submittedAt: Date;
}

export interface AffidavitSubmissionMetadata {
    firstName: string;
    lastName: string;
    companyName: string;
    registrationNumber: string;
    formattedAddress: string;
}

export const useAffidavitStore = defineStore('affidavit', () => {
    const affidavitApi = new AffidavitApi(apiConfiguration);
    const {handleError} = useErrorHandler();

    const affidavitData = ref<AffidavitData | null>(null);
    async function fetchAffidavitPdfData(affidavitId: string): Promise<AffidavitData> {
        const {data, error} = await useAsyncData(`affidavit-pdf-metadata-${affidavitId}`, () => affidavitApi.getAffidavitPdfMetadata({affidavitId}));

        if (error.value) {
            handleError(error.value.message);
            throw error.value;
        }

        const metadata = data.value;
        if (!metadata) {
            throw new Error(`Missing affidavit metadata for affidavit ${affidavitId}`);
        }

        const result: AffidavitData = {
            firstName: metadata.firstName,
            lastName: metadata.lastName,
            companyName: metadata.companyName,
            registrationNumber: metadata.registrationNumber,
            formattedAddress: metadata.formattedAddress,
            submittedAt: metadata.submittedAt,
        };
        affidavitData.value = result;
        return result;
    }

    async function fetchMetadataForSubmission(companyId: string): Promise<AffidavitSubmissionMetadata> {
        const result = await affidavitApi.getAffidavitMetadata({companyId});
        return {
            firstName: result.firstName,
            lastName: result.lastName,
            companyName: result.companyName,
            registrationNumber: result.registrationNumber,
            formattedAddress: result.formattedAddress,
        };
    }

    async function submitAffidavit(companyId: string, documentType: DocumentTypeResource, locale: string): Promise<void> {
        await affidavitApi.submitAffidavit({
            affidavitSubmissionRequest: {companyId, documentType, locale},
        });
    }

    return {
        affidavitData,
        fetchMetadataForSubmission,
        submitAffidavit,
        fetchAffidavitPdfData,
    };
});
